import React, { useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "./ChatContext";
import { API_URL } from "../config";

const formatTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const escapeRegExp = (str = "") => String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export default function ChatWindow() {
  const {
    connected,
    loading,
    users,
    groups,
    unread,
    groupNotices,
    activeChat,
    messages,
    typing,
    me,
    openConversationWith,
    openGroupConversation,
    sendMessage,
    sendMedia,
    deleteMessage,
    togglePinMessage,
    forwardMessage,
    downloadChatBackup,
    createGroup,
    addGroupMembers,
    removeGroupMember,
    leaveGroup,
    deleteGroup,
    startTyping,
    stopTyping,
  } = useChat();

  const [input, setInput] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [recording, setRecording] = useState(false);
  const [manageGroupOpen, setManageGroupOpen] = useState(false);
  const [groupAddSelections, setGroupAddSelections] = useState([]);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardMessageId, setForwardMessageId] = useState("");
  const [forwardType, setForwardType] = useState("direct");
  const [forwardTargetId, setForwardTargetId] = useState("");

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const attachInputRef = useRef(null);

  const activeTypingKey = useMemo(() => {
    if (!activeChat) return "";
    return activeChat.type === "group" ? `group:${activeChat.id}` : String(activeChat.id);
  }, [activeChat]);

  const isEmployeeUser = ["employee", "intern", "trainee"].includes(
    String(me?.role || "").toLowerCase()
  );
  const isSuperadmin = String(me?.role || "").toLowerCase() === "superadmin";
  const canPinMessages = ["superadmin", "admin", "manager"].includes(
    String(me?.role || "").toLowerCase()
  );
  const activeGroup =
    activeChat?.type === "group"
      ? groups.find((g) => String(g._id) === String(activeChat.id))
      : null;
  const activeGroupMembers = useMemo(() => {
    if (!activeGroup) return [];

    const details = Array.isArray(activeGroup.memberDetails)
      ? activeGroup.memberDetails.filter(Boolean)
      : [];

    if (details.length > 0) return details;

    const fallbackIds = Array.isArray(activeGroup.members) ? activeGroup.members.map(String) : [];
    const usersMap = new Map((users || []).map((u) => [String(u._id), u]));

    return fallbackIds.map((id) => {
      const match = usersMap.get(id);
      return {
        _id: id,
        name: match?.name || id,
        role: match?.role || "member",
      };
    });
  }, [activeGroup, users]);
  const addableUsers = activeGroup
    ? users.filter(
        (u) => !(activeGroup.members || []).map(String).includes(String(u._id))
      )
    : [];

  const mentionCandidates = useMemo(() => {
    if (activeChat?.type !== "group") return [];
    const q = mentionQuery.trim().toLowerCase();
    return (activeGroupMembers || [])
      .filter((m) => String(m._id) !== String(me?.id))
      .filter((m) => !q || String(m.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [activeChat, activeGroupMembers, mentionQuery, me?.id]);

  const mentionDropdownOpen =
    activeChat?.type === "group" &&
    mentionStartIndex >= 0 &&
    mentionCandidates.length > 0;

  useEffect(() => {
    setSelectedMentions([]);
    setMentionQuery("");
    setMentionStartIndex(-1);
  }, [activeChat?.id, activeChat?.type]);

  const forwardTargets = useMemo(() => {
    if (forwardType === "group") {
      return groups.map((g) => ({
        id: String(g._id),
        name: g.name || "Unnamed Group",
      }));
    }

    return users.map((u) => ({
      id: String(u._id),
      name: u.name || "Unknown User",
    }));
  }, [forwardType, groups, users]);

  const activePinnedMessages = useMemo(() => {
    if (activeChat?.type !== "group") return [];
    return (messages || [])
      .filter((m) => m?.isPinned)
      .sort((a, b) => new Date(b.pinnedAt || b.updatedAt || 0) - new Date(a.pinnedAt || a.updatedAt || 0))
      .slice(0, 5);
  }, [activeChat, messages]);

  const onSend = async () => {
    if (!input.trim()) return;
    const mentionsToSend =
      activeChat?.type === "group"
        ? selectedMentions.map((m) => ({ userId: String(m.userId), name: m.name }))
        : [];

    const result = await sendMessage(input.trim(), { mentions: mentionsToSend });
    if (!result?.success) return;
    setInput("");
    setSelectedMentions([]);
    setMentionQuery("");
    setMentionStartIndex(-1);
    stopTyping();
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await sendMedia(file);
    if (!result?.success) {
      e.target.value = "";
      return;
    }
    e.target.value = "";
  };

  const onAttachClick = () => {
    if (!activeChat) {
      alert("Select a chat first");
      return;
    }
    attachInputRef.current?.click();
  };

  const startVoiceRecording = async () => {
    try {
      if (!activeChat) return;
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        alert("Voice recording is not supported in this browser.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const preferredType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")
        ? "audio/ogg;codecs=opus"
        : "";

      const recorder = preferredType
        ? new MediaRecorder(stream, { mimeType: preferredType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        try {
          const type = recorder.mimeType || "audio/webm";
          const blob = new Blob(chunksRef.current, { type });
          const ext = type.includes("ogg") ? "ogg" : type.includes("mp4") ? "m4a" : "webm";
          const voiceFile = new File([blob], `voice-note-${Date.now()}.${ext}`, { type });
          await sendMedia(voiceFile);
        } catch (err) {
          alert("Failed to send voice note.");
        } finally {
          chunksRef.current = [];
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
          }
          mediaRecorderRef.current = null;
        }
      };

      recorder.start();
      setRecording(true);
    } catch (error) {
      alert("Microphone permission denied or unavailable.");
    }
  };

  const stopVoiceRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } finally {
      setRecording(false);
    }
  };

  const toggleVoiceRecording = async () => {
    if (!activeChat) return;
    if (recording) {
      stopVoiceRecording();
      return;
    }
    await startVoiceRecording();
  };

  const onCreateGroup = async () => {
    if (!groupName.trim() || selectedMembers.length < 1) {
      alert("Group name and at least 1 member are required.");
      return;
    }

    await createGroup({
      name: groupName.trim(),
      memberIds: selectedMembers,
    });

    setGroupName("");
    setSelectedMembers([]);
    setCreatingGroup(false);
  };

  const toggleMember = (memberId) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  const toggleGroupAddSelection = (memberId) => {
    setGroupAddSelections((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleAddMembersToActiveGroup = async () => {
    if (!activeGroup?._id || !groupAddSelections.length) return;
    await addGroupMembers(activeGroup._id, groupAddSelections);
    setGroupAddSelections([]);
  };

  const handleRemoveMemberFromActiveGroup = async (memberId) => {
    if (!activeGroup?._id || !memberId) return;
    await removeGroupMember(activeGroup._id, memberId);
  };

  const handleDeleteActiveGroup = async () => {
    if (!activeGroup?._id) return;
    if (!window.confirm(`Delete group "${activeGroup.name}"?`)) return;
    await deleteGroup(activeGroup._id);
  };

  const handleLeaveActiveGroup = async () => {
    if (!activeGroup?._id) return;
    if (!window.confirm(`Leave group "${activeGroup.name}"?`)) return;
    await leaveGroup(activeGroup._id);
  };

  const handleInputChange = (value) => {
    setInput(value);

    if (value) startTyping();
    else stopTyping();

    setSelectedMentions((prev) =>
      prev.filter((m) => value.toLowerCase().includes(`@${String(m.name || "").toLowerCase()}`))
    );

    if (activeChat?.type !== "group") {
      setMentionQuery("");
      setMentionStartIndex(-1);
      return;
    }

    const lastAt = value.lastIndexOf("@");
    if (lastAt < 0) {
      setMentionQuery("");
      setMentionStartIndex(-1);
      return;
    }

    const after = value.slice(lastAt + 1);
    const hasWhitespace = /\s/.test(after);
    if (hasWhitespace) {
      setMentionQuery("");
      setMentionStartIndex(-1);
      return;
    }

    setMentionQuery(after);
    setMentionStartIndex(lastAt);
  };

  const onMentionSelect = (member) => {
    const safeName = String(member?.name || "").trim();
    if (!safeName || mentionStartIndex < 0) return;
    const before = input.slice(0, mentionStartIndex);
    const next = `${before}@${safeName} `;
    setInput(next);
    setSelectedMentions((prev) => {
      const existing = prev.find((m) => String(m.userId) === String(member._id));
      if (existing) return prev;
      return [...prev, { userId: String(member._id), name: safeName }];
    });
    setMentionQuery("");
    setMentionStartIndex(-1);
  };

  const renderMessageText = (msg, mine) => {
    const text = String(msg?.message || "");
    const mentions = Array.isArray(msg?.mentions) ? msg.mentions : [];
    if (!mentions.length || !text) return <div>{text}</div>;

    const uniqueNames = Array.from(
      new Set(
        mentions
          .map((m) => String(m?.name || "").trim())
          .filter(Boolean)
      )
    );
    if (!uniqueNames.length) return <div>{text}</div>;

    const pattern = uniqueNames.map((n) => `@${escapeRegExp(n)}`).join("|");
    if (!pattern) return <div>{text}</div>;

    const regex = new RegExp(`(${pattern})`, "gi");
    const parts = text.split(regex);

    return (
      <div>
        {parts.map((part, idx) => {
          const isMention = /^@/.test(part) && uniqueNames.some((n) => part.toLowerCase() === `@${n.toLowerCase()}`);
          if (!isMention) return <React.Fragment key={`${part}-${idx}`}>{part}</React.Fragment>;
          return (
            <span
              key={`${part}-${idx}`}
              style={{
                background: mine ? "rgba(255,255,255,0.2)" : "#dbeafe",
                color: mine ? "#fff" : "#1e3a8a",
                padding: "0 4px",
                borderRadius: 4,
                fontWeight: 600,
              }}
            >
              {part}
            </span>
          );
        })}
      </div>
    );
  };

  const renderReceipt = (msg, mine) => {
    if (!mine || activeChat?.type !== "direct") return null;
    const seen = Boolean(msg?.read);
    const delivered = Boolean(msg?.deliveredAt || msg?.read);
    return (
      <span
        title={seen ? "Seen" : delivered ? "Delivered" : "Sent"}
        style={{
          marginLeft: 8,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.2,
          color: seen ? "#60a5fa" : mine ? "rgba(255,255,255,0.85)" : "#64748b",
        }}
      >
        {seen ? "✓✓" : delivered ? "✓✓" : "✓"}
      </span>
    );
  };

  const openForwardDialog = (messageId) => {
    if (!messageId) return;
    setForwardMessageId(String(messageId));
    setForwardType("direct");
    setForwardTargetId("");
    setForwardOpen(true);
  };

  const submitForward = async () => {
    if (!forwardMessageId || !forwardTargetId) {
      alert("Please select a target to forward");
      return;
    }

    const result = await forwardMessage({
      messageId: forwardMessageId,
      targetType: forwardType,
      targetId: forwardTargetId,
    });

    if (!result?.success) {
      alert(result?.message || "Failed to forward message");
      return;
    }

    setForwardOpen(false);
    setForwardMessageId("");
    setForwardTargetId("");
    alert("Message forwarded successfully");
  };

  const handleDownloadBackup = async (format = "json") => {
    const result = await downloadChatBackup(activeChat, format);
    if (!result?.success) {
      alert(result?.message || "Failed to download backup");
    }
  };

  const handleTogglePin = async (msg) => {
    if (!msg?._id) return;
    const result = await togglePinMessage(msg._id, !msg.isPinned);
    if (!result?.success) {
      alert(result?.message || "Failed to update pin");
    }
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "280px 1fr",
        height: "100%",
        minHeight: 0,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          borderRight: "1px solid #e5e7eb",
          overflowY: "scroll",
          minHeight: 0,
          scrollbarGutter: "stable",
        }}
      >
        <div style={{ padding: 12, borderBottom: "1px solid #eee" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong>Chats</strong>
            <span style={{ color: connected ? "#16a34a" : "#6b7280", fontSize: 12 }}>
              {connected ? "online" : "offline"}
            </span>
          </div>
        </div>

        <div style={{ padding: "8px 12px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: "#475569" }}>Groups</span>
            {!isEmployeeUser ? (
              <button
                type="button"
                onClick={() => setCreatingGroup((v) => !v)}
                style={{ border: 0, background: "#003e6d", color: "#fff", borderRadius: 6, padding: "2px 8px" }}
              >
                +
              </button>
            ) : null}
          </div>

          {!isEmployeeUser && creatingGroup ? (
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 8, marginBottom: 8 }}>
                <input
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Group name"
                  style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 6, padding: 6, marginBottom: 6 }}
                />
                <div style={{ maxHeight: 120, overflowY: "auto", border: "1px solid #f1f5f9", borderRadius: 6, padding: 6 }}>
                  {users.map((u) => (
                    <label key={u._id} style={{ display: "block", fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(u._id)}
                        onChange={() => toggleMember(u._id)}
                        style={{ marginRight: 6 }}
                      />
                      {u.name}
                    </label>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={onCreateGroup}
                  style={{ marginTop: 8, width: "100%", border: 0, background: "#111827", color: "#fff", borderRadius: 6, padding: "6px 8px" }}
                >
                  Create Group
                </button>
              </div>
            ) : null}

          {groupNotices?.length > 0 ? (
            <div style={{ marginBottom: 8 }}>
              {groupNotices.slice(0, 2).map((n) => (
                <div
                  key={n.id}
                  style={{
                    fontSize: 11,
                    color: "#1e3a8a",
                    background: "#dbeafe",
                    borderRadius: 6,
                    padding: "6px 8px",
                    marginBottom: 6,
                  }}
                >
                  {n.text}
                </div>
              ))}
            </div>
          ) : null}

          {groups.map((group) => {
            const key = `group:${group._id}`;
            const isActive = activeChat?.type === "group" && String(activeChat?.id) === String(group._id);
            const memberNames = (group.memberDetails || [])
              .map((m) => m.name)
              .filter(Boolean);
            const memberCount = Array.isArray(group.memberDetails) && group.memberDetails.length > 0
              ? group.memberDetails.length
              : (group.members?.length || 0);
            return (
              <div
                key={group._id}
                onClick={() => openGroupConversation(group)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: isActive ? "#eff6ff" : "transparent",
                  marginBottom: 4,
                }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{group.name}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>
                    {memberCount} members
                    {unread[key] ? ` • ${unread[key]} unread` : ""}
                  </div>
                  {memberNames.length > 0 ? (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 11,
                        color: "#475569",
                        lineHeight: 1.35,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      }}
                    >
                      Members: {memberNames.join(", ")}
                    </div>
                  ) : null}
                </div>
              );
            })}
        </div>

        <div style={{ padding: "8px 12px" }}>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 6 }}>Direct</div>
          {users.map((u) => {
            const isActive = activeChat?.type === "direct" && String(activeChat?.id) === String(u._id);
            return (
              <div
                key={u._id}
                onClick={() => openConversationWith(u._id, u.name)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 10px",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: isActive ? "#eef2ff" : "transparent",
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: u.isOnline ? "#16a34a" : "#9ca3af",
                    marginRight: 8,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>{u.role}</div>
                </div>
                {unread[u._id] ? (
                  <span style={{ fontSize: 11, background: "#dc2626", color: "#fff", borderRadius: 12, padding: "2px 6px" }}>
                    {unread[u._id]}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", position: "relative" }}>
        <div
          style={{
            padding: 12,
            borderBottom: "1px solid #e5e7eb",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>
            {activeChat ? activeChat.name || (activeChat.type === "group" ? "Group" : "Direct Chat") : "Select chat"}
          </span>
          {isSuperadmin && activeGroup ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => handleDownloadBackup("json")}
                style={{
                  border: 0,
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: "#111827",
                  color: "#fff",
                  fontSize: 12,
                }}
              >
                Backup JSON
              </button>
              <button
                type="button"
                onClick={() => handleDownloadBackup("csv")}
                style={{
                  border: 0,
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: "#334155",
                  color: "#fff",
                  fontSize: 12,
                }}
              >
                Backup CSV
              </button>
              <button
                type="button"
                onClick={() => setManageGroupOpen((v) => !v)}
                style={{
                  border: 0,
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: "#003e6d",
                  color: "#fff",
                  fontSize: 12,
                }}
              >
                {manageGroupOpen ? "Hide Manage" : "Manage Group"}
              </button>
              <button
                type="button"
                onClick={handleDeleteActiveGroup}
                style={{
                  border: 0,
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: "#b91c1c",
                  color: "#fff",
                  fontSize: 12,
                }}
              >
                Delete Group
              </button>
            </div>
          ) : activeGroup ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => handleDownloadBackup("json")}
                style={{
                  border: 0,
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: "#111827",
                  color: "#fff",
                  fontSize: 12,
                }}
              >
                Backup JSON
              </button>
              <button
                type="button"
                onClick={() => handleDownloadBackup("csv")}
                style={{
                  border: 0,
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: "#334155",
                  color: "#fff",
                  fontSize: 12,
                }}
              >
                Backup CSV
              </button>
              <button
                type="button"
                onClick={handleLeaveActiveGroup}
                style={{
                  border: 0,
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: "#475569",
                  color: "#fff",
                  fontSize: 12,
                }}
              >
                Leave Group
              </button>
            </div>
          ) : activeChat ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => handleDownloadBackup("json")}
                style={{
                  border: 0,
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: "#111827",
                  color: "#fff",
                  fontSize: 12,
                }}
              >
                Backup JSON
              </button>
              <button
                type="button"
                onClick={() => handleDownloadBackup("csv")}
                style={{
                  border: 0,
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: "#334155",
                  color: "#fff",
                  fontSize: 12,
                }}
              >
                Backup CSV
              </button>
            </div>
          ) : null}
        </div>

        {activeChat?.type === "group" && activePinnedMessages.length > 0 ? (
          <div
            style={{
              borderBottom: "1px solid #e5e7eb",
              background: "#fff7ed",
              padding: "8px 12px",
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: "#9a3412", marginBottom: 6 }}>
              Pinned Messages
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              {activePinnedMessages.map((m) => (
                <div
                  key={`pinned-${m._id}`}
                  style={{
                    fontSize: 12,
                    color: "#7c2d12",
                    background: "#ffedd5",
                    border: "1px solid #fed7aa",
                    borderRadius: 6,
                    padding: "6px 8px",
                  }}
                >
                  <strong>{m.senderId === me?.id ? "You" : "Member"}:</strong> {String(m.message || "").slice(0, 160)}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {isSuperadmin && activeGroup && manageGroupOpen ? (
          <div style={{ padding: 10, borderBottom: "1px solid #e5e7eb", background: "#f8fafc" }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Members</div>
            <div style={{ maxHeight: 110, overflowY: "auto", marginBottom: 8 }}>
              {activeGroupMembers.map((member) => (
                <div
                  key={member._id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "4px 0",
                    fontSize: 12,
                  }}
                >
                  <span>
                    {member.name} ({member.role})
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveMemberFromActiveGroup(member._id)}
                    style={{
                      border: 0,
                      borderRadius: 6,
                      background: "#fee2e2",
                      color: "#b91c1c",
                      padding: "2px 8px",
                      fontSize: 11,
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Add Members</div>
            <div style={{ maxHeight: 110, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: 6 }}>
              {addableUsers.length === 0 ? (
                <div style={{ fontSize: 12, color: "#64748b" }}>No available users to add.</div>
              ) : (
                addableUsers.map((u) => (
                  <label key={u._id} style={{ display: "block", fontSize: 12 }}>
                    <input
                      type="checkbox"
                      checked={groupAddSelections.includes(u._id)}
                      onChange={() => toggleGroupAddSelection(u._id)}
                      style={{ marginRight: 6 }}
                    />
                    {u.name} ({u.role})
                  </label>
                ))
              )}
            </div>
            <button
              type="button"
              onClick={handleAddMembersToActiveGroup}
              disabled={groupAddSelections.length === 0}
              style={{
                marginTop: 8,
                border: 0,
                borderRadius: 6,
                padding: "6px 10px",
                background: groupAddSelections.length ? "#111827" : "#9ca3af",
                color: "#fff",
                fontSize: 12,
              }}
            >
              Add Selected
            </button>
          </div>
        ) : null}

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            background: "#f8fafc",
            padding: 12,
            scrollbarGutter: "stable",
          }}
        >
          {!activeChat ? (
            <div style={{ color: "#64748b" }}>Select a user or group to start chat.</div>
          ) : loading ? (
            <div style={{ color: "#64748b" }}>Loading messages...</div>
          ) : messages.length === 0 ? (
            <div style={{ color: "#64748b" }}>No messages yet.</div>
          ) : (
            messages.map((msg) => {
              const mine = String(msg.senderId) === String(me?.id);
              return (
                <div key={msg._id} style={{ textAlign: mine ? "right" : "left", marginBottom: 10 }}>
                  <div
                    style={{
                      display: "inline-block",
                      maxWidth: "75%",
                      borderRadius: 12,
                      padding: "8px 10px",
                      background: mine ? "#003e6d" : "#e5e7eb",
                      color: mine ? "#fff" : "#111827",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {msg.isForwarded ? (
                      <div style={{ fontSize: 10, opacity: 0.75, marginBottom: 4 }}>
                        Forwarded
                      </div>
                    ) : null}
                    {msg.media?.url ? (
                      msg.messageType === "photo" ? (
                        <img
                          src={`${API_URL}${msg.media.url}`}
                          alt={msg.media.originalName || "media"}
                          style={{ maxWidth: 220, maxHeight: 220, borderRadius: 8, objectFit: "cover" }}
                        />
                      ) : msg.messageType === "video" ? (
                        <video
                          src={`${API_URL}${msg.media.url}`}
                          controls
                          style={{ maxWidth: 260, maxHeight: 220, borderRadius: 8 }}
                        />
                      ) : msg.messageType === "voice" ? (
                        <audio
                          src={`${API_URL}${msg.media.url}`}
                          controls
                          style={{ maxWidth: 260 }}
                        />
                      ) : (
                        <a
                          href={`${API_URL}${msg.media.url}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: mine ? "#fff" : "#1d4ed8", textDecoration: "underline" }}
                        >
                          {msg.media.originalName || "Attachment"}
                        </a>
                      )
                    ) : null}
                    {renderMessageText(msg, mine)}
                    <div style={{ fontSize: 10, opacity: 0.8, marginTop: 4 }}>
                      {formatTime(msg.timestamp)}
                      {renderReceipt(msg, mine)}
                    </div>
                  </div>
                  {me?.role === "superadmin" ? (
                    <div>
                      <button
                        type="button"
                        onClick={() => deleteMessage(msg._id)}
                        style={{
                          marginTop: 4,
                          border: 0,
                          background: "#fee2e2",
                          color: "#b91c1c",
                          borderRadius: 6,
                          fontSize: 11,
                          padding: "2px 6px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  ) : null}
                  <div>
                    <button
                      type="button"
                      onClick={() => openForwardDialog(msg._id)}
                      style={{
                        marginTop: 4,
                        border: 0,
                        background: "#e2e8f0",
                        color: "#334155",
                        borderRadius: 6,
                        fontSize: 11,
                        padding: "2px 6px",
                        cursor: "pointer",
                      }}
                    >
                      Forward
                    </button>
                  </div>
                  {activeChat?.type === "group" && canPinMessages ? (
                    <div>
                      <button
                        type="button"
                        onClick={() => handleTogglePin(msg)}
                        style={{
                          marginTop: 4,
                          border: 0,
                          background: msg?.isPinned ? "#fee2e2" : "#ede9fe",
                          color: msg?.isPinned ? "#991b1b" : "#5b21b6",
                          borderRadius: 6,
                          fontSize: 11,
                          padding: "2px 6px",
                          cursor: "pointer",
                        }}
                      >
                        {msg?.isPinned ? "Unpin" : "Pin"}
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}

          {typing[activeTypingKey] ? (
            <div style={{ fontSize: 12, color: "#64748b" }}>Typing...</div>
          ) : null}
        </div>

        <div
          style={{
            padding: 10,
            borderTop: "1px solid #e5e7eb",
            display: "grid",
            gridTemplateColumns: "40px minmax(0, 1fr) 44px auto",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
          }}
        >
          <button
            type="button"
            onClick={onAttachClick}
            title={activeChat ? "Attach file" : "Select a chat first"}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "8px 10px",
              cursor: "pointer",
              background: activeChat ? "#fff" : "#f3f4f6",
              color: "#374151",
            }}
          >
            +
          </button>
          <input
            ref={attachInputRef}
            type="file"
            accept="image/*,video/*,audio/*,application/pdf,.xls,.xlsx,.ppt,.pptx,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            hidden
            onChange={onFileChange}
          />
          <input
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            disabled={!activeChat}
            placeholder={activeChat ? "Type your message..." : "Select a chat first"}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSend();
              }
            }}
            style={{ flex: 1, border: "1px solid #d1d5db", borderRadius: 8, padding: "10px 12px" }}
          />
          <button
            type="button"
            onClick={toggleVoiceRecording}
            disabled={!activeChat}
            title={recording ? "Stop recording" : "Record voice note"}
            style={{
              border: 0,
              borderRadius: 8,
              background: recording ? "#dc2626" : "#0ea5e9",
              color: "#fff",
              padding: "0 14px",
              minWidth: 44,
              height: 42,
            }}
          >
            {recording ? (
              "■"
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M12 15a3 3 0 0 0 3-3V7a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M19 11a7 7 0 0 1-14 0"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 18v3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M8 21h8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={!activeChat || !input.trim()}
            style={{
              border: 0,
              borderRadius: 8,
              background: "#1f2937",
              color: "#fff",
              padding: "0 16px",
              minWidth: 74,
              height: 42,
              whiteSpace: "nowrap",
            }}
          >
            Send
          </button>
        </div>
        {mentionDropdownOpen ? (
          <div
            style={{
              position: "absolute",
              bottom: 64,
              left: 58,
              right: 120,
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
              zIndex: 100001,
              maxHeight: 180,
              overflowY: "auto",
            }}
          >
            {mentionCandidates.map((m) => (
              <button
                key={m._id}
                type="button"
                onClick={() => onMentionSelect(m)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  border: 0,
                  background: "#fff",
                  padding: "8px 10px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                @{m.name}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {forwardOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 100001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(460px, 100%)",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              padding: 16,
            }}
          >
            <h6 style={{ marginBottom: 12 }}>Forward Message</h6>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: "#475569", display: "block", marginBottom: 6 }}>
                Target Type
              </label>
              <select
                value={forwardType}
                onChange={(e) => {
                  setForwardType(e.target.value);
                  setForwardTargetId("");
                }}
                style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: 8 }}
              >
                <option value="direct">Direct User</option>
                <option value="group">Group</option>
              </select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: "#475569", display: "block", marginBottom: 6 }}>
                Forward To
              </label>
              <select
                value={forwardTargetId}
                onChange={(e) => setForwardTargetId(e.target.value)}
                style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: 8 }}
              >
                <option value="">Select target</option>
                {forwardTargets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={() => setForwardOpen(false)}
                style={{
                  border: 0,
                  borderRadius: 8,
                  padding: "8px 12px",
                  background: "#e2e8f0",
                  color: "#334155",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitForward}
                style={{
                  border: 0,
                  borderRadius: 8,
                  padding: "8px 12px",
                  background: "#003e6d",
                  color: "#fff",
                }}
              >
                Forward
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}



